import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import onlyNumbers from '../../../../../../../utils/onlyNumbers';
import useErrors from '../../../../../../../hooks/useErrors';
import isYearValid from '../../../../../../../utils/isYearValid';
import isValueValid from '../../../../../../../utils/isValueValid';
import useGetGeneralAppData from '../../../../../../../hooks/useGetGeneralAppData';
import useApiCall from '../../../../../../../hooks/useApiCall';
import VehiclesService from '../../../../../../../services/affreightment/VehiclesService';
import { MultiValue } from 'react-select';

export default function useInputChanges() {
  const [model, setModel] = useState<number | string>('');
  const [transportType, setTransportType] = useState<{ value: number, label: string }>({} as { value: number, label: string });
  const [transportTypeValue, setTransportTypeValue] = useState<number | string>('');
  const [vehicleCode, setVehicleCode] = useState('');
  const [brand, setBrand] = useState('');
  const [capacity, setCapacity] = useState<number | string>('');
  const [consumption, setConsumption] = useState<number | string>('');
  const [workplace, setWorkplace] = useState<{ value: number, label: string }>({} as { value: number, label: string });
  const [affreightmentLines, setAffreightmentLines] = useState<MultiValue<{ value: string, label: string }>>([]);

  const [isSelectLoading, setIsSelectLoading] = useState(false);
  const [isLinesSelectLoading, setIsLinesSelectLoading] = useState(false);
  const [isLinesSelectDisabled, setIsLinesSelectDisabled] = useState(true);

  const [linesOptions, setLinesOptions] = useState<MultiValue<{ value: string, label: string }>>([]);

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const {
    userCode,
    userProfile,
    companyCode,
  } = useGetGeneralAppData();

  const { apiCall } = useApiCall();

  const getLinesOfWorkplace = useCallback(async (workplaceCode: number | string) => {
    await apiCall({
      apiToCall: VehiclesService.getLines,
      queryParams: {
        codLocTrab: workplaceCode,
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
      },
      onStartLoad: () => {
        setIsSelectLoading(true);
        setIsLinesSelectLoading(true);
      },
      onEndLoad: () => {
        setIsSelectLoading(false);
        setIsLinesSelectLoading(false);
      },
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível recuperar as linhas do local de trabalho selecionado (${response.msg})`);
          setLinesOptions([]);
          setIsLinesSelectDisabled(true);
          return;
        }
        const lines: { routeID: string, letreiro: string }[] = response.linhasFretado;
        const lnhOptions: MultiValue<{ value: string, label: string }> = lines?.map((line) => (
          { value: line.routeID, label: line.letreiro }
        ));
        setIsLinesSelectDisabled(false);
        setLinesOptions(response.listaLinhas ? lnhOptions : [{ value: '', label: 'Não existem linhas associadas à este local de trabalho' }]);
      }

    });
  }, [apiCall, companyCode, setIsLinesSelectLoading, setIsSelectLoading, setLinesOptions, userCode, userProfile]);

  function handleModelChange(event: ChangeEvent<HTMLInputElement>) {
    setModel(onlyNumbers(event.target.value));

    if (!isYearValid(Number(event.target.value), 2000) || !event.target.value) {
      setError({ field: 'model', message: 'Ano deve ser de 2000 ao ano atual' });
    } else {
      removeError('model');
    }
  }

  function handleVehicleCodeChange(event: ChangeEvent<HTMLInputElement>) {
    setVehicleCode(event.target.value);
    if (!event.target.value) {
      setError({ field: 'vehicleCode', message: 'Código do veículo é obrigatório' });
    } else {
      removeError('vehicleCode');
    }
  }

  function handleBrandChange(event: ChangeEvent<HTMLInputElement>) {
    setBrand(event.target.value);
    if (!event.target.value) {
      setError({ field: 'brand', message: 'Marca do veículo é obrigatória' });
    } else {
      removeError('brand');
    }
  }

  function handleCapacityChange(event: ChangeEvent<HTMLInputElement>) {
    setCapacity(onlyNumbers(event.target.value));

    if (!isValueValid(Number(event.target.value), 7, 50) || !event.target.value) {
      setError({ field: 'capacity', message: 'Capacidade deve ser entre 7 e 50' });
    } else {
      removeError('capacity');
    }
  }

  function handleConsumptionChange(event: ChangeEvent<HTMLInputElement>) {
    setConsumption(onlyNumbers(event.target.value));

    if (!isValueValid(Number(event.target.value), 5, 20) || !event.target.value) {
      setError({ field: 'consumption', message: 'Consumo deve ser entre 5 e 20' });
    } else {
      removeError('consumption');
    }
  }

  async function handleLocTrabChange({ value, label }: { value: number, label: string }) {
    setWorkplace({ value, label });
    await getLinesOfWorkplace(value);
    setAffreightmentLines([]);
  }

  function handleLinesChange(opt: MultiValue<{ value: string, label: string }>) {
    setAffreightmentLines(opt);
  }

  return {
    setIsSelectLoading,
    setIsLinesSelectLoading,
    setAffreightmentLines,
    setWorkplace,
    setModel,
    setTransportType,
    setTransportTypeValue,
    setVehicleCode,
    setBrand,
    setCapacity,
    setConsumption,
    setIsLinesSelectDisabled,
    model,
    transportType,
    transportTypeValue,
    vehicleCode,
    brand,
    capacity,
    consumption,
    workplace,
    affreightmentLines,
    isSelectLoading,
    isLinesSelectLoading,
    isLinesSelectDisabled,
    getErrorMessageByFieldName,
    handleModelChange,
    handleVehicleCodeChange,
    handleBrandChange,
    handleCapacityChange,
    handleConsumptionChange,
    handleLocTrabChange,
    handleLinesChange,
    errors,
    linesOptions,
    getLinesOfWorkplace,
  };
}
